<template>
  <section
    id="createOrder"
    class="d-flex justify-center">
    <v-form
      v-if="pageReady"
      ref="orderForm"
      @submit.prevent="updateOrder()">
      <v-row>
        <v-col
          ref="orderTitle"
          cols="12"
          class="order-form-content-box">
          <h1>
            Order Form
          </h1>
        </v-col>
        <v-col
          cols="12"
          class="order-form-content-box">
          <h2 class="mb-2">
            Sketch Information
          </h2>
          <v-row>
            <v-col cols="12">
              <v-simple-table style="border: 1px solid #000;">
                <template v-slot:default>
                  <tbody>
                    <tr>
                      <th>Sketch ID</th>
                      <td>
                        <span v-if="sketch.sketchId">{{ sketch.sketchId }}</span>
                      </td>
                      <th>Type / Category </th>
                      <td>
                        <span v-if="sketch.category && sketch.category.name">{{ sketch.category.name }}</span>
                      </td>
                    </tr>
                    <tr>
                      <th>Collection</th>
                      <td>
                        <span v-if="sketch.collectionName">{{ sketch.collectionName }}</span>
                      </td>
                      <th>Classification </th>
                      <td>
                        <span v-if="sketch.classification">{{ sketch.classification }}</span>
                      </td>
                    </tr>
                    <tr>
                      <th>Factory</th>
                      <td>
                        <span v-if="sketch.factory && sketch.factory.name">{{ sketch.factory.name }}</span>
                      </td>
                      <th>Designer </th>
                      <td>
                        <span v-if="sketch.designer && sketch.designer.name">{{ sketch.designer.name }}</span>
                      </td>
                    </tr>
                    <tr>
                      <th>Merchandiser</th>
                      <td>
                        <span v-if="sketch.merchandiser && sketch.merchandiser.name">{{ sketch.merchandiser.name }}</span>
                      </td>
                      <th>Graphic </th>
                      <td>
                        <span v-if="sketch.graphic && sketch.graphic.name">{{ sketch.graphic.name }}</span>
                      </td>
                    </tr>
                    <tr>
                      <th>Creative / Stylist</th>
                      <td>
                        <span v-if="sketch.stylist && sketch.stylist.name">{{ sketch.stylist.name }}</span>
                      </td>
                      <th>Visual Merchandiser</th>
                      <td>
                        <span v-if="sketch.visualMerchandiser && sketch.visualMerchandiser.name">{{ sketch.visualMerchandiser.name }}</span>
                      </td>
                    </tr>
                    <tr>
                      <th>Marketing</th>
                      <td>
                        <span v-if="sketch.marketing && sketch.marketing.name">{{ sketch.marketing.name }}</span>
                      </td>
                      <th>Producer</th>
                      <td>
                        <span v-if="sketch.producer && sketch.producer.name">{{ sketch.producer.name }}</span>
                      </td>
                    </tr>
                    <tr>
                      <th> Date Added </th>
                      <td> {{ sketch.createdAt | dateTimeFormat() }}</td>
                      <th style="vertical-align: top; padding-top: 10px; padding-bottom: 10px;">
                        Note
                      </th>
                      <td
                        colspan="3"
                        style="vertical-align: top; white-space: pre-line; padding-top: 10px; padding-bottom: 10px;">
                        {{ sketch.note }}
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-col>
            <v-col
              v-for="(image, index) in sketch.images"
              :key="`sketchImg-${index}`"
              md="4"
              cols="12"
              class="detail-box-image-box">
              <img
                :src="$convertImgix(`${image}?w=600`)"
                class="detail-box-image"
                @click="() => { previewImage = $convertImgix(`${image}?w=1024`); previewEnabled = true; }" />
            </v-col>
          </v-row>
        </v-col>
        <v-col
          cols="12"
          class="order-form-content-box">
          <h2 class="mb-2">
            Order Information
          </h2>
          <v-text-field
            v-model="form.productId"
            outlined
            label="Product ID"
            dense
            :disabled="!!sketch.sketchNo" />
          <v-text-field
            v-model="form.productFactoryId"
            outlined
            disabled
            label="Product Factory ID"
            dense />
          <v-select
            v-model="form.factory"
            :items="selectOptions.factories"
            item-text="name"
            return-object
            outlined
            label="Factory *"
            :rules="selectRules"
            dense />
          <v-select
            v-model="form.subCategory"
            :items="selectOptions.subCategories"
            label="Sub-Categories *"
            item-text="name"
            item-value="id"
            outlined
            :rules="selectRules"
            return-object
            dense />
          <date-with-menu
            v-model="form.orderDate"
            label="Order Date"
            :default-date="$dayjs().format('YYYY-MM-DD')"
            dense />
          <date-with-menu
            v-model="form.targetDate"
            label="Target Date"
            :default-date="$dayjs().add(1, 'month').format('YYYY-MM-DD')"
            dense />
          <v-checkbox
            v-model="form.requirePrintCareLabel"
            label="ต้องปรินท์ Care label"></v-checkbox>
          <v-checkbox
            v-model="form.requirePrintFabricLabel"
            label="ต้องปรินท์ Fabric label"></v-checkbox>
        </v-col>
        <v-col
          cols="12"
          class="order-form-content-box">
          <h2>
            Fittings
          </h2>
          <v-row
            v-for="(fitting, index) in form.fitting"
            :key="`fitting-${index}`">
            <v-col
              cols="12"
              class="pb-0">
              <p class="mb-0">
                Fitting #{{ index + 1 }}
              </p>
            </v-col>
            <v-col
              cols="6"
              class="pt-0">
              <gw-drop-file
                v-model="fitting.images"
                :multiple="true"
                need-compress
                :limit="20"
                s3-path="/sketch-images"
                caption="คลิกเพื่อเพิ่มรูปภาพ"
                label-bold />
            </v-col>
            <v-col
              cols="6"
              class="mt-0">
              <v-textarea
                v-model="fitting.note"
                outlined
                label="Note"
                hide-details />
              <v-btn
                v-if="form.fitting.length > 1"
                color="error"
                class="delete-fitting-btn mt-4"
                @click="deleteFitting(index)">
                <v-icon>
                  mdi-delete
                </v-icon>
              </v-btn>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <v-btn
                color="primary"
                style="width: 100%;"
                @click="addFitting()">
                <v-icon left>
                  mdi-plus
                </v-icon>
                เพิ่ม Fitting
              </v-btn>
            </v-col>
          </v-row>
        </v-col>
        <v-col
          cols="12"
          class="order-form-content-box">
          <h2>
            Product Sku
          </h2>
          <v-combobox
            v-model="sizeDetail"
            label="Size *"
            class="mt-2"
            :items="sizes"
            multiple
            outlined
            deletable-chips
            small-chips
            dense
            :rules="arrayRules" />
          <v-combobox
            v-model="colorsSkus"
            label="Color *"
            :items="colors"
            multiple
            outlined
            deletable-chips
            small-chips
            dense
            :rules="arrayRules" />
        </v-col>
      </v-row>
      <v-row
        v-for="(item, i) in form.colors"
        :key="i">
        <v-col
          cols="12"
          class="order-form-content-box">
          <p
            class="field-label text-uppercase"
            style="font-size: 20px;">
            {{ item.color }} - Fabrics
          </p>
          <fabric-stage
            v-model="form.colors[i].fabrics"
            :required-composition="form.requirePrintFabricLabel"
            show-amount
            :form-validates="fabricFormValidates[i]"
            @onFillComposition="(v) => onFillComposition(v)" />
        </v-col>
        <v-col
          cols="12"
          class="order-form-content-box">
          <p
            class="field-label text-uppercase"
            style="font-size: 20px;">
            {{ item.color }} - Materials
          </p>
          <material-stage
            v-model="form.colors[i].materials"
            show-amount
            :form-validates="materialFormValidates" />
        </v-col>
      </v-row>
      <v-row>
        <v-col
          cols="12"
          class=" white my-4 pa-8 elevation-2 rounded">
          <h2>Garment Type</h2>
          <v-select
            v-model="form.garmentType"
            class="mt-4"
            dense
            :items="selectOptions.garmentType"
            :menu-props="{ offsetY: true }"
            label="Garment Type"
            item-text="name"
            item-value="value"
            outlined
            hide-details
          />
        </v-col>
        <v-col
          cols="12"
          class=" white my-4 pa-8 elevation-2 rounded">
          <h2> Care Label </h2>
          <v-select
            v-model="form.careLabel"
            class="mt-4"
            dense
            :items="selectOptions.careLabels"
            :menu-props="{ offsetY: true }"
            label="Care Label"
            item-text="name"
            return-object
            outlined
            hide-details
            @change="(v) => getCareLabelProductExample(v.id)" />
          <div v-if="form.careLabel">
            <img
              class="mt-4"
              :src="$convertImgix(`${form.careLabel.imageUrl}?w=1024`)"
              width="300"
              alt="">
          </div>
          <div v-if="productExamples.length">
            <p class="text-bold mt-4">
              Latest items with this Care Label
            </p>
            <div
              class="d-flex flex-wrap my-4">
              <v-card
                v-for="(product, index) in productExamples"
                :key="`example-${index}`"
                elevation="2"
                max-width="120"
                class="mx-2"
              >
                <v-img
                  :src="$convertImgix(`${product.photoUrl}?w=1024`)"
                  :alt="`example-${index}`"
                  height="80%"
                />

                <v-card-text
                  class="py-2 text-truncate text-center"
                  style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis;"
                >
                  {{ product.name }}
                </v-card-text>
              </v-card>
            </div>
          </div>
          <div v-if="loadingExample">
            <p class="text-bold mt-4">
              Latest items with this Care Label
            </p>
            <v-icon
              large
              class="mdi-spin"
              color="primary">
              mdi-loading
            </v-icon>
          </div>
        </v-col>
      </v-row>
      <v-row>
        <v-col
          cols="12"
          class="order-form-content-box">
          <h2> Costing </h2>
          <manufac-calculate
            v-model="form"
            :margin="skuRatio"
            :is-thailand="isThailand" />
        </v-col>
        <v-col
          cols="12"
          class="order-form-content-box">
          <h2> SKU Production QTY </h2>
          <sku-detail
            ref="skuDetail"
            v-model="form" />
        </v-col>
        <v-col
          cols="12"
          class="order-form-content-box">
          <h2> Sizing </h2>
          <sizing-detail
            v-model="form.sizeDetail"
            class="mt-4"
            :size-unit.sync="form.sizeUnit" />
          <v-btn
            color="success"
            class="mt-4"
            style="width: 100%;"
            @click="updateOrder()">
            SAVE
          </v-btn>
        </v-col>
      </v-row>
    </v-form>
    <img-popup-preview
      v-model="previewEnabled"
      :img-src="previewImage" />
  </section>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import GetImageOrGradientCss from '@/assets/js/GetImageOrGradientCss'
import FactoryProvider from '@/resources/FactoryProvider'
import PrototypeOrderProvider from '@/resources/PrototypeOrderProvider'
import ImgPopupPreview from '@/components/ImgModalPreview.vue'
// import PrototypeProvider from '@/resources/PrototypeProvider'
import ProductAttributeProvider from '@/resources/ProductAttributeProvider'
import PrototypeSubCategoryProvider from '@/resources/PrototypeSubCategoryProvider'
import PrototypeCurrency from '@/assets/json/PrototypeCurrency.json'
import MaterialStage from '../components/MaterialStage.vue'
import DateWithMenu from '../components/DateWithMenu.vue'
import ManufacCalculate from '../components/ManufactureCalculate.vue'
import SkuDetail from '../components/SkuDetail.vue'
import SizingDetail from '../components/SizingDetail.vue'
import FabricStage from '../components/FabricStage.vue'
import CareLabelProvider from '@/resources/CareLabelProvider'
import FabricsProvider from '@/resources/FabricsProvider'

const FactoryService = new FactoryProvider()
const PrototypeOrderService = new PrototypeOrderProvider()
// const PrototypeService = new PrototypeProvider()
const ProductAttributeService = new ProductAttributeProvider()
const PrototypeSubCategoryService = new PrototypeSubCategoryProvider()
const CareLabelService = new CareLabelProvider()

export default {
  components: {
    MaterialStage,
    DateWithMenu,
    ManufacCalculate,
    ImgPopupPreview,
    SkuDetail,
    SizingDetail,
    FabricStage
  },
  data () {
    return {
      fabricService: new FabricsProvider(),
      textBoxRules: [
        (v) => !!v || 'This field is required'
      ],
      selectRules: [
        (v) => (!!v && !!v.id) || 'This field is required'
      ],
      previewEnabled: false,
      previewImage: null,
      assistData: {
        productId: null,
        productFactoryId: null
      },
      pageReady: false,
      form: {
        fitting: [
          {
            images: [],
            note: ''
          }
        ],
        productId: null,
        productFactoryId: null,
        factory: {
          id: null,
          name: 'ไม่เลิือก'
        },
        orderDate: this.$dayjs().format('YYYY-MM-DD'),
        targetDate: this.$dayjs().add(1, 'month').format('YYYY-MM-DD'),
        manufacCountry: null,
        sellPrice: null,
        manufacCost: null,
        sizeDetail: [],
        sizeUnit: '',
        productSkus: [],
        collectionName: null,
        classification: null,
        merchandiser: null,
        designer: null,
        graphic: null,
        stylist: null,
        replacement: [],
        ratio: null,
        colors: [],
        subCategory: {
          id: null,
          name: '',
          parentId: null
        },
        careLabel: null
      },
      selectOptions: {
        factories: [],
        colors: [],
        sizes: [],
        subCategories: [],
        careLabels: [],
        garmentType: [
          {
            name: 'Knit',
            value: 'knit'
          },
          {
            name: 'Woven',
            value: 'woven'
          },
          {
            name: 'No Garment',
            value: 'no garment'
          }
        ]
      },
      countries: PrototypeCurrency,
      sketch: {},
      prototypeState: '',
      arrayRules: [
        (v) => Boolean(v?.length || false) || 'Field is require!'
      ],
      materialFormValidates: [],
      fabricFormValidates: [],
      CNY_MULTIPLE: 5,
      productExamples: [],
      loadingExample: false
    }
  },
  computed: {
    ...mapGetters({
      recentlyRoute: 'Route/recentlyRoute'
    }),
    factories () {
      return this.selectOptions.factories
    },
    colors () {
      return this.selectOptions.colors.map((s) => s.name)
    },
    sizes () {
      return this.selectOptions.sizes.map((s) => s.name)
    },
    priceWithoutVat () {
      return this.form?.sellPrice ? this.form.sellPrice / 1.07 : 0
    },
    skuRatio () {
      const ratio = Number(parseFloat(this.priceWithoutVat / this.totalCostPrice).toFixed(2)) || 0
      this.mapRatio(ratio)
      return ratio
    },
    isThailand () {
      return this.form.manufacCountry === 'thailand'
    },
    mappedCurrency () {
      return this.countries.reduce((acc, c) => ({ ...acc, [c.currency]: c.multiple }), {})
    },
    country () {
      return this.countries.find((c) => c.value === this.form.manufacCountry)
    },
    currencyMultiple () {
      return this.country?.multiple || 1
    },
    materialsPrice () {
      const allMaterials = this.form?.colors?.reduce((arr, c) => [...arr, ...c.materials], []) || []

      return allMaterials.reduce((acc, cur) => acc + (cur.amount * cur.unitPrice * this.mappedCurrency[cur.currency]), 0) || 0
    },
    fabricsPrice () {
      const allFabrics = this.form?.colors?.reduce((arr, c) => [...arr, ...c.fabrics], []) || []

      return allFabrics.reduce((acc, cur) => acc + (cur.amount * cur.unitPrice * this.mappedCurrency[cur.currency]), 0) || 0
    },
    totalCostPrice () {
      const cost = (this.form.manufacCost * this.currencyMultiple) + this.materialsPrice + this.fabricsPrice || 0

      this.mapCost(cost)

      return cost
    },
    prototypeId () {
      return this.$route?.params?.id || ''
    },
    sizeDetail: {
      get () {
        return this.form.sizeDetail.map((item) => item.size.trim())
      },
      set (newVal) {
        const newArr = [...new Set(newVal)].filter((s) => this.sizes.includes(s))
        this.setSizeDetail(newArr)
        this.setProductSkus(this.colorsSkus, newArr)
      }
    },
    colorsSkus: {
      get () {
        return this.form.colors.map((item) => item.color.trim())
      },
      set (newVal) {
        const newArr = [...new Set(newVal)].filter((c) => this.colors.includes(c))
        this.setProductSkus(newArr, this.sizeDetail)
        this.setColors(newArr)
      }
    }
  },
  watch: {
    'form.colors': {
      handler (newData) {
        this.materialFormValidates = newData.map((c) => c.materials.map((mat) => this.cleanMaterials([mat], false)))
        this.validateFabric(newData)
      },
      deep: true
    }
  },
  created () {
    this.initForm()
  },
  methods: {
    ...mapActions({
      setSnackbar: 'Components/setSnackbar',
      setModal: 'Components/setModal',
      setLoading: 'Components/setLoading',
      setDarkMode: 'Style/setDarkMode',
      setIsWebpSupported: 'Style/setIsWebpSupported',
      initStore: 'Store/initStore'
    }),
    mapRatio (ratio = this.skuRatio) {
      this.form.productSkus.forEach((sku, index) => {
        this.form.productSkus[index].ratio = ratio
      })
    },
    mapCost (cost = this.totalCostPrice) {
      this.form.productSkus.forEach((sku, index) => {
        this.form.productSkus[index].cost = cost
      })
    },
    setColors (newColors) {
      const { productSkus, colors } = this.form
      const result = newColors.map((color) => {
        const colorData = this.selectOptions.colors.find((c) => c.name === color)
        const skus = productSkus.filter((sku) => sku.color === color)
        const oldData = colors.find((sku) => sku.color === color)
        const materials = oldData?.materials || this.sketch?.materials || [
          {
            materialId: '',
            imageUrls: [],
            name: '',
            factory: '',
            material: '',
            size: '',
            color: '',
            collectionName: '',
            unitPrice: 0,
            currency: 'thb',
            amount: 0,
            note: '',
            patterns: [{
              note: '',
              imageUrls: []
            }]
          }
        ]

        const fabrics = oldData?.fabrics || this.sketch?.fabrics || [
          {
            hangId: '',
            fabricNo: '',
            imageUrls: [],
            factory: null,
            size: '',
            color: '',
            note: '',
            collectionName: '',
            amount: 0,
            currency: 'thb',
            unitPrice: 0,
            patterns: [{
              note: '',
              imageUrls: []
            }]
          }
        ]

        return {
          id: colorData.id,
          color,
          totalQty: oldData?.totalQty || skus.reduce((sum, sku) => sum + Number(sku.quantity || 0), 0),
          materials: materials.map((material) => ({
            ...material,
            patterns: material?.patterns || [{
              note: '',
              imageUrls: []
            }]
          })),
          fabrics
        }
      })

      this.form.colors = JSON.parse(JSON.stringify(result))
    },
    setProductSkus (colors, sizes) {
      const { productSkus } = this.form
      const skusLength = productSkus.length
      const result = []
      colors.forEach((color) => {
        const colorData = this.selectOptions.colors.find((c) => c.name === color)

        sizes.forEach((size) => {
          const sizeData = this.selectOptions.sizes.find((s) => s.name === size)
          const oldData = productSkus.find((sku) => sku.color === color && sku.size === size)
          const data = {
            colorId: colorData.id,
            color,
            sizeId: sizeData.id,
            size,
            quantity: oldData?.quantity || null,
            ratio: this.skuRatio || 0,
            cost: this.totalCostPrice || 0,
            percent: oldData?.percent || Number((100 / sizes.length).toFixed(2)),
            productSellsuki: oldData?.productSellsuki || null
          }

          if (!(data?.productSellsuki?.productId)) {
            delete data.productSellsuki
          }

          result.push(data)
        })
      })
      this.form.productSkus.splice(0, skusLength, ...result)
    },
    setSizeDetail (newArr) {
      if (newArr.length >= this.form.sizeDetail.length) {
        newArr.forEach((size) => {
          const findIndex = this.form.sizeDetail.findIndex((item) => item.size === size)
          if (this.form.sizeDetail.length > 0 && findIndex !== -1) {
            this.form.sizeDetail.splice(findIndex, 1, {
              ...this.form.sizeDetail[findIndex]
            })
          } else if (this.form.sizeDetail.length > 0) {
            this.form.sizeDetail.push({
              options: this.form.sizeDetail[0].options.map((option) => ({
                key: option?.key || '',
                value: ''
              })),
              size
            })
          } else {
            this.form.sizeDetail.push({
              options: [{
                key: '',
                value: ''
              }],
              size
            })
          }
        })
      } else {
        this.form.sizeDetail.forEach((item, index) => {
          const findIndex = newArr.findIndex((size) => item.size === size)
          if (findIndex === -1) {
            this.form.sizeDetail.splice(index, 1)
          }
        })
      }
    },
    async updateOrder () {
      try {
        this.setLoading({
          active: true,
          clickAble: false,
          message: 'UPDATE ORDER...'
        })

        if (!this.sketch.category) {
          this.setSnackbar({
            value: true,
            message: 'Please fill category / type in sketch before edit order.',
            type: 'error'
          })

          this.setLoading({ active: false })

          return
        }

        const { data: createdProduct } = await PrototypeOrderService.checkPrototypeProduct({ id: this.prototypeId })
        if (createdProduct && createdProduct.length && this.form.requirePrintFabricLabel) {
          const missCompositions = this.form?.colors
            .flatMap((item) => item.fabrics)
            .find((fab) => fab.compositions.length === 0 && fab.printInFabricLabel === true)
          if (missCompositions) {
            this.setSnackbar({
              value: true,
              message: 'กรุณาระบุ Compositions ให้ครบก่อนทำการบันทึก',
              timeout: 5000,
              type: 'error'
            })
            return
          }
        }

        if (createdProduct && createdProduct.length && this.form.requirePrintCareLabel && !this.form.careLabel) {
          this.setSnackbar({
            value: true,
            message: 'กรุณาระบุ Care Label',
            timeout: 5000,
            type: 'error'
          })
          return
        }

        const valid = await this.$refs.orderForm.validate()
        if (!valid) {
          const el = this.$refs.orderTitle
          if (el) {
            el.scrollIntoView({ behavior: 'smooth' })
          }

          return
        }
        const skus = this.form?.productSkus.reduce((acc, cur) => {
          const found = acc.find((item) => item.color === cur.color)
          if (found) {
            found.total += cur.percent
          } else {
            acc.push({ color: cur.color, total: cur.percent })
          }
          return acc
        }, [])

        const colorPercent = skus.map((sk) => {
          if (sk.total !== 100) {
            return sk.color
          }
          return ''
        }).filter((res) => res)

        if (valid && !colorPercent.length) {
          // const { data: existedProductId } = await PrototypeService.getPrototypeByProductId(this.form.productId)

          // if (existedProductId && this.assistData.productId !== this.form.productId) {
          //   const error = {
          //     code: 400,
          //     message: 'Duplicate Product Id.'
          //   }
          //   throw error
          // }

          // const { data: existedProductFactoryId } = await PrototypeService.getPrototypeByProductFactoryId(this.form.productFactoryId)

          // if (existedProductFactoryId && this.assistData.productFactoryId !== this.form.productFactoryId) {
          //   const error = {
          //     code: 400,
          //     message: 'Duplicate Product Factory Id.'
          //   }

          //   throw error
          // }

          const mappedForm = {
            ...this.form,
            collectionName: this.sketch.collectionName || this.form.collectionName || '',
            classification: this.sketch.classification || this.form.classification || ''
          }
          const productSkus = mappedForm.productSkus.map((sku) => {
            const colorSku = this.colorsSkus.find((c) => c === sku.color)
            const colorData = this.selectOptions.colors.find((c) => c.name === sku.color)
            const sizeData = this.selectOptions.sizes.find((s) => s.name === sku.size)

            return {
              colorId: colorData.id,
              color: colorData?.name || colorSku,
              sizeId: sizeData.id,
              size: sizeData.name || sku.size,
              quantity: sku?.quantity || null,
              ratio: this.skuRatio || 0,
              cost: this.totalCostPrice || 0,
              percent: sku?.percent || null,
              productSellsuki: colorSku?.productSellsuki || null
            }
          })

          mappedForm.productSkus = [...productSkus]

          const payload = this.removeUnuseData(mappedForm)

          await PrototypeOrderService.editOrder(this.prototypeId, this.setFormatOrder(payload))

          this.setSnackbar({
            value: true,
            message: 'Prototype has been updated',
            type: 'success'
          })

          this.redirectRoute()
        } else {
          this.$refs['skuDetail'].$el.scrollIntoView({ behavior: 'smooth' })
          if (colorPercent.length) {
            this.setSnackbar({
              value: true,
              message: `${colorPercent} is more or less 100 %`,
              type: 'error'
        })
          }
        }
      } catch (error) {
        console.error('updateOrder', error)
        this.setSnackbar({
          value: true,
          message: `${error.code}: ${error.message}`,
          type: 'error'
        })
      } finally {
        this.setLoading({ active: false })
      }
    },
    redirectRoute () {
      switch (this.prototypeState) {
        case 'order':
          if (this.recentlyRoute?.name === 'OrderFormList') {
            this.$router.push(this.recentlyRoute)
          } else {
            this.$router.push({ name: 'OrderFormList' })
          }
          break
        case 'manufacturing':
          if (this.recentlyRoute?.name === 'ManufactureList') {
            this.$router.push(this.recentlyRoute)
          } else {
            this.$router.push({ name: 'ManufactureList' })
          }
          break
        default:
          this.$router.push({ name: 'OrderFormList' })
          break
      }
    },
    async getOrder () {
      try {
        const { data } = await PrototypeOrderService.getOneOrderById(this.prototypeId)
        this.form = {
          ...this.form,
          ...data.order,
          requirePrintCareLabel: data.requirePrintCareLabel ?? true,
          requirePrintFabricLabel: data.requirePrintFabricLabel ?? true
        }
        this.form.colors = this.form.colors.map((color) => {
          const materials = color.materials.length
            ? color.materials
            : [
              {
                sketchId: '',
                materialId: '',
                imageUrls: [],
                name: '',
                factory: '',
                color: '',
                size: '',
                material: '',
                unitPrice: 0,
                amount: 0,
                currency: 'thb',
                collectionName: '',
                note: '',
                patterns: [{
                  note: '',
                  imageUrls: []
                }]
              }
            ]

          const fabrics = color.fabrics.length
            ? color.fabrics
            : [
              {
                sketchId: '',
                hangId: '',
                fabricId: '',
                imageUrls: [],
                factory: '',
                unitPrice: 0,
                currency: 'thb',
                amount: 0,
                note: '',
                patterns: [{
                  imageUrls: [],
                  note: ''
                }]
              }
            ]

          const colorData = this.selectOptions.colors.find((c) => c.name === color.color)

          return {
            ...color,
            id: colorData.id,
            materials,
            fabrics
          }
        })
        this.sketch = {
          ...data.sketch,
          materials: data.sketch.materials.length
            ? data.sketch.materials
            : [
              {
                sketchId: '',
                materialId: '',
                imageUrls: [],
                name: '',
                factory: '',
                color: '',
                size: '',
                material: '',
                unitPrice: 0,
                amount: 0,
                currency: 'thb',
                collectionName: '',
                note: '',
                patterns: [{
                  note: '',
                  imageUrls: []
                }]
              }
            ],
          fabrics: data.sketch.fabrics.length
            ? data.sketch.fabrics
            : [
              {
                sketchId: '',
                hangId: '',
                fabricId: '',
                imageUrls: [],
                factory: '',
                unitPrice: 0,
                currency: 'thb',
                amount: 0,
                note: '',
                patterns: [{
                  imageUrls: [],
                  note: ''
                }],
                garmentPart: null,
                printInFabricLabel: true
              }
            ]
        }
        this.prototypeState = data.state || 'order'

        this.assistData = {
          productId: this.form.productId,
          productFactoryId: this.form.productFactoryId
        }

        this.initDefaultObject()
      } catch (error) {
        console.error('getOrder', error)
        this.setSnackbar({
          value: true,
          message: `${error.code}: ${error.message}`,
          type: 'error'
        })
      }
    },
    async initSelectOptions () {
      try {
        const [factoryPromise, attributePromise, subCategoriesPromise, careLabel] = await Promise.all([
          FactoryService.getAll(),
          ProductAttributeService.getAllProductAttribute(),
          PrototypeSubCategoryService.getAllMapped(),
          CareLabelService.getAll()
        ])

        this.selectOptions.factories = factoryPromise.data.results.sort((a, b) => a.id - b.id)
        this.selectOptions.colors = attributePromise.data.colors ? this.getColor(attributePromise.data.colors).sort((a, b) => a.id - b.id) : []
        this.selectOptions.sizes = attributePromise.data.size ? attributePromise.data.size.sort((a, b) => a.id - b.id) : []
        this.selectOptions.subCategories = subCategoriesPromise.data.map((d) => ({
          ...d,
          disabled: d.name === 'UNKNOWN'
        }))
        this.selectOptions.careLabels = careLabel.data ? careLabel.data : []
      } catch (error) {
        console.error('initSelectOptions', error)
      }
    },
    getColor (arr, itemsDisabled) {
      return arr.map((color) => ({
        ...color,
        label: color.name,
        children: color.childs && color.childs.length > 0 ? this.getColor(color.childs, itemsDisabled) : undefined,
        gradientCss: GetImageOrGradientCss(color)
      }))
    },
    initDefaultObject () {
      const fitting = (this.form?.fitting && !Array.isArray(this.form.fitting)) ? [this.form.fitting] : [...this.form.fitting]
      this.form.fitting = fitting.length ? fitting : [{ images: [], note: '' }]
      this.form.materials = this.form?.materials?.length > 0 ? this.form.materials.map((material) => ({
        ...material,
        imageUrls: material?.imageUrls ? material.imageUrls : material.images,
        unitPrice: material.unitPrice ? material.unitPrice : 0,
        currency: material.currency ? material.currency : 'thb',
        amount: material.amount ? material.amount : 0,
        patterns: material?.patterns || [{
          note: '',
          imageUrls: []
        }]
      })) : [
        {
          sketchId: '',
          materialId: '',
          imageUrls: [],
          name: '',
          factory: '',
          color: '',
          size: '',
          material: '',
          unitPrice: 0,
          amount: 0,
          currency: 'thb',
          collectionName: '',
          note: '',
          patterns: [{
            note: '',
            imageUrls: []
          }]
        }
      ]
      this.form.fabrics = this.form?.fabrics?.length > 0 ? this.form.fabrics.map((fabric) => ({
        ...fabric,
        unitPrice: fabric.unitPrice ? fabric.unitPrice : 0,
        currency: fabric.currency ? fabric.currency : 'thb',
        amount: fabric.amount ? fabric.amount : 0
      })) : [
        {
          sketchId: '',
          hangId: '',
          fabricId: '',
          imageUrls: [],
          factory: '',
          unitPrice: 0,
          currency: 'thb',
          amount: 0,
          note: '',
          patterns: [{
            imageUrls: [],
            note: ''
          }],
          garmentPart: null,
          printInFabricLabel: true
        }
      ]
      this.form.factory = this.form?.factory || { name: null, id: null }
    },
    async initForm () {
      try {
        this.setLoading({
          active: true,
          clickAble: false,
          message: 'GETTING ORDER...'
        })

        await this.initSelectOptions()
        await this.getOrder(this.prototypeId)

        this.pageReady = true
      } catch (error) {
        console.error(error)
      } finally {
        this.setLoading({ active: false })
      }
    },
    removeUnuseData (_obj) {
      const obj = _obj
      const keys = Object.keys(obj)

      if ('colors' in obj) {
        obj.colors = obj.colors.map((c) => {
          const tmpColor = { ...c }

          if ('materials' in tmpColor) {
            tmpColor.materials = this.cleanMaterials(tmpColor.materials)
          }

          if ('fabrics' in tmpColor) {
            tmpColor.fabrics = this.cleanFabrics(tmpColor.fabrics)
          }

          return tmpColor
        })
      }

      keys.forEach((key) => {
        if (key !== 'orderDate' && key !== 'targetDate' && key !== 'productSellsuki' && key !== 'options') {
          if (obj[key] && typeof obj[key] === 'object') {
            obj[key] = this.removeUnuseData(obj[key])
          } else if (
            obj[key] === undefined
            || obj[key] === null
            || (typeof obj[key] === 'string' && obj[key] === '')
            || (typeof obj[key] === 'object' && JSON.stringify(obj[key]) === '{}')
            || (typeof obj[key] === 'boolean' && obj[key] === undefined)
          ) {
            delete obj[key]
          }
        }
      })

      return JSON.stringify(obj) !== '{}' ? obj : null
    },
    cleanMaterials (materials, isClean = true) {
      const checkKeys = [
        {
          key: 'sketchId',
          type: 'string'
        },
        {
          key: 'materialId',
          type: 'string'
        },
        {
          key: 'imageUrls',
          type: 'array'
        },
        {
          key: 'name',
          type: 'string'
        },
        // {
        //   key: 'factory',
        //   type: 'string'
        // },
        {
          key: 'color',
          type: 'string'
        },
        {
          key: 'size',
          type: 'string'
        },
        {
          key: 'material',
          type: 'string'
        },
        {
          key: 'unitPrice',
          type: 'number'
        },
        {
          key: 'amount',
          type: 'number'
        },
        {
          key: 'currency',
          type: 'string',
          default: 'thb'
        },
        {
          key: 'collectionName',
          type: 'string'
        },
        // {
        //   key: 'patterns',
        //   type: 'array',
        //   default: [
        //     {
        //       note: '',
        //       imageUrls: []
        //     }
        //   ]
        // },
        {
          key: 'note',
          type: 'string'
        }
      ]

      if (isClean) {
        return materials
          .filter((mat) => !checkKeys.every((ck) => {
            switch (ck.type) {
              case 'string': return mat[ck.key] === '' || mat[ck.key] === ck?.default
              case 'number': return mat[ck.key] === 0 || mat[ck.key] === ck?.default
              case 'array': return mat[ck.key].length === 0 || JSON.stringify(mat[ck.key]) === JSON.stringify(ck?.default)
              default: return false
            }
          }))
          .map((mat) => ({
            ...mat,
            sketchId: this.sketch.sketchId || mat?.sketchId?.toUpperCase() || '',
            name: mat?.name?.toUpperCase() || '',
            factory: mat?.factory?.toUpperCase() || '',
            color: mat?.color?.toUpperCase() || '',
            size: mat?.size?.toUpperCase() || '',
            material: mat?.material?.toUpperCase() || '',
            collectionName: this.sketch.collectionName || mat?.collectionName?.toUpperCase() || ''
          }))
      }

      return materials.every((mat) => checkKeys.every((ck) => {
          switch (ck.type) {
            case 'string': return mat[ck.key] === '' || mat[ck.key] === ck?.default
            case 'number': return mat[ck.key] === 0 || mat[ck.key] === ck?.default
            case 'array': return mat[ck.key].length === 0 || JSON.stringify(mat[ck.key]) === JSON.stringify(ck?.default)
            default: return false
          }
        }))
    },
    cleanFabrics (fabrics, isClean = true) {
      const checkKeys = [
        {
          key: 'sketchId',
          type: 'string'
        },
        {
          key: 'hangId',
          type: 'string'
        },
        {
          key: 'fabricNo',
          type: 'string'
        },
        {
          key: 'imageUrls',
          type: 'array'
        },
        // {
        //   key: 'factory',
        //   type: 'string'
        // },
        {
          key: 'size',
          type: 'string'
        },
        {
          key: 'color',
          type: 'string'
        },
        {
          key: 'unitPrice',
          type: 'number'
        },
        {
          key: 'amount',
          type: 'number'
        },
        {
          key: 'currency',
          type: 'string',
          default: 'thb'
        },
        {
          key: 'collectionName',
          type: 'string'
        },
        // {
        //   key: 'patterns',
        //   type: 'array',
        //   default: [
        //     {
        //       note: '',
        //       imageUrls: []
        //     }
        //   ]
        // },
        {
          key: 'note',
          type: 'string'
        },
        {
          key: 'garmentPart',
          type: 'object',
          default: null
        },
        {
          key: 'printInFabricLabel',
          type: 'boolean',
          default: true
        }
      ]

      if (isClean) {
        return fabrics
        .filter((fab) => !checkKeys.every((ck) => {
          switch (ck.type) {
            case 'string': return fab[ck.key] === '' || fab[ck.key] === ck?.default
            case 'number': return fab[ck.key] === 0 || fab[ck.key] === ck?.default
            case 'array': return fab[ck.key].length === 0 || JSON.stringify(fab[ck.key]) === JSON.stringify(ck?.default)
            case 'object': return fab[ck.key] === ck?.default
            case 'boolean': return ck?.default
            default: return false
          }
        }))
        .map((fab) => ({
          ...fab,
          sketchId: this.sketch.sketchId || fab?.sketchId?.toUpperCase() || '',
          hangId: fab?.hangId?.toUpperCase() || '',
          fabricNo: fab?.fabricNo?.toUpperCase() || '',
          factory: fab?.factory?.toUpperCase() || '',
          size: fab?.size?.toUpperCase() || '',
          color: fab?.color?.toUpperCase() || '',
          collectionName: this.sketch.collectionName || fab?.collectionName?.toUpperCase() || ''
        }))
      }

      return fabrics.every((fab) => checkKeys.every((ck) => {
          switch (ck.type) {
            case 'string': return fab[ck.key] === '' || fab[ck.key] === ck?.default
            case 'number': return fab[ck.key] === 0 || fab[ck.key] === ck?.default
            case 'array': return fab[ck.key].length === 0 || JSON.stringify(fab[ck.key]) === JSON.stringify(ck?.default)
            case 'object': return fab[ck.key] === ck?.default
            case 'boolean': return ck?.default
            default: return false
          }
        }))
    },
    addFitting () {
      this.form.fitting.push({
        images: [],
        note: ''
      })
    },
    deleteFitting (index) {
      this.form.fitting.splice(index, 1)
    },
    setFormatOrder (orders) {
      const colors = orders.colors.map((color) => {
        const materials = color.materials.filter((mat) => ((mat.id && mat.name) || (!mat.id && mat.name)))
        const fabrics = color.fabrics.filter((fab) => ((fab.id && fab.hangId && fab.fabricNo) || (!fab.id && fab.hangId && fab.fabricNo)))
        return {
          ...color,
          materials,
          fabrics,
          requirePrintCareLabel: this.form.requirePrintCareLabel,
          requirePrintFabricLabel: this.form.requirePrintFabricLabel
        }
      })

      return { ...orders, colors, careLabel: orders?.careLabel?.id || null }
    },
    async getCareLabelProductExample (careLabelId) {
      this.loadingExample = true
      this.productExamples = []
      const { data } = await PrototypeOrderService.getCareLabelProductExample(careLabelId)
      this.productExamples = data
      this.loadingExample = false
    },
    onFillComposition (fabric) {
      if (!fabric) return
      this.form.colors.forEach((color, colorIndex) => {
        color.fabrics.forEach((fb, fbIndex) => {
          if (fb.hangId === fabric.hangId) {
             this.form.colors[colorIndex].fabrics[fbIndex]['compositions'] = fabric.compositions
          }
        })
      })
    },
    validateFabric (fabricData) {
      this.fabricFormValidates = fabricData.map((c) => c.fabrics.map((fab) => this.cleanFabrics([fab], false)))
      const flatValidates = this.fabricFormValidates.flatMap((fab) => fab)
      if (flatValidates.length && flatValidates.includes(false) && this.$refs.orderForm) {
        this.$refs.orderForm.validate()
      }
    }
  }
}
</script>

<style scoped>
.delete-fitting-btn.v-btn {
  height: 40px !important;
}
#createOrder {
  max-width: 100vw;
}
.order-form-content-box {
  padding: 15px;
  border-radius: 6px;
  margin-top: 16px;
  box-shadow: 0px 3px 3px -2px rgba(0, 0, 0, 0.2), 0px 3px 4px 0px rgba(0, 0, 0, 0.14), 0px 1px 8px 0px rgba(0, 0, 0, 0.12) !important;
  background-color: #fff;
}
.create-order-form {
  margin: 16px 0px;
}
.field-label {
  font-weight: bold;
  margin: 0px;
}
.capitalize {
  text-transform: capitalize;
}
.dash-border {
  border: 2px dashed #555555;
  border-radius: 5px;
}
img.detail-box-image {
  cursor: pointer;
  transition: transform 0.1s;
}
img.detail-box-image:active {
  transform: scale(0.95);
}
</style>
